import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { legacyAppUrl } from '@/lib/api'
import { applyTheme, getStoredTheme, useLegadoVisual, type Theme } from '@/lib/legadoVisual'
import { navForPerfil, type AppNavItem } from '@/lib/rbac'
import './shell.css'

/**
 * Shell React sobre o markup/CSS do legado (projeto/index.html + js/core/Sidebar.js):
 * `.sidebar` (logo, busca, "pinned", setores em acordeão), `.sidebar-overlay`,
 * `.topbar` (breadcrumb, tema, usuário + dropdown) e `.page-container`.
 */

const SECTORS_KEY = 'sidebar_open_sectors' // mesma chave do Sidebar.js
const COLLAPSED_KEY = 'sidebar_collapsed' // mesma chave do app.js
const MOBILE_MAX = 768

function readOpenSectors(): string[] {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SECTORS_KEY) || '[]')
    return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : []
  } catch {
    return []
  }
}

export function AppShell() {
  useLegadoVisual()

  const { user, loading, isAuthenticated, logout, perfilLabel, canView } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => sessionStorage.getItem(COLLAPSED_KEY) === '1')
  const [openSectors, setOpenSectors] = useState<string[]>(readOpenSectors)
  const [search, setSearch] = useState('')
  const [theme, setTheme] = useState<Theme>(getStoredTheme)
  const [userMenu, setUserMenu] = useState(false)
  const userRef = useRef<HTMLDivElement>(null)

  const nav = useMemo(() => navForPerfil(user?.perfil), [user?.perfil])
  const { pinned, groups } = useMemo(() => splitNav(nav), [nav])

  const persistOpen = useCallback((next: string[]) => {
    setOpenSectors(next)
    sessionStorage.setItem(SECTORS_KEY, JSON.stringify(next))
  }, [])

  // Fecha overlay/menu ao navegar.
  useEffect(() => {
    setMobileOpen(false)
    setUserMenu(false)
  }, [location.pathname])

  // Abre o setor da rota ativa (como Sidebar.setActive).
  useEffect(() => {
    const active = groups.find((g) =>
      g.items.some((item) => item.to && pathMatches(location.pathname, item.to, item.end)),
    )
    if (active) {
      setOpenSectors((prev) => {
        if (prev.length === 1 && prev[0] === active.label) return prev
        sessionStorage.setItem(SECTORS_KEY, JSON.stringify([active.label]))
        return [active.label]
      })
    }
  }, [location.pathname, groups])

  // Fecha dropdown do usuário: clique fora / Esc.
  useEffect(() => {
    if (!userMenu) return
    function onDown(e: MouseEvent) {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenu(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setUserMenu(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [userMenu])

  if (loading) {
    return <div className="hi-shell-loading">Carregando sessão…</div>
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const nome = user?.nome || user?.name || user?.email || 'Usuário'
  const iniciais = initials(nome)
  const crumb = crumbFromPath(location.pathname, nav)
  const query = search.trim().toLowerCase()

  function toggleCollapse() {
    setCollapsed((v) => {
      const next = !v
      sessionStorage.setItem(COLLAPSED_KEY, next ? '1' : '0')
      return next
    })
  }

  // ☰ no desktop colapsa; no mobile abre o drawer (igual app.js#toggleSidebar).
  function toggleSidebar() {
    if (window.innerWidth <= MOBILE_MAX) setMobileOpen((v) => !v)
    else toggleCollapse()
  }

  function toggleSector(label: string) {
    if (collapsed) {
      // Sidebar colapsada: expande e abre só esse setor.
      setCollapsed(false)
      sessionStorage.setItem(COLLAPSED_KEY, '0')
      persistOpen([label])
      return
    }
    persistOpen(openSectors.length === 1 && openSectors[0] === label ? [] : [label])
  }

  function toggleTheme() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    applyTheme(next)
  }

  async function sair() {
    setUserMenu(false)
    await logout()
    navigate('/login', { replace: true })
  }

  const showPinned = pinned.filter((item) => matchesQuery(item, query))

  return (
    <div className="app-shell-root">
      <a href="#pageContainer" className="skip-to-content">
        Pular para o conteúdo principal
      </a>

      <aside
        className={`sidebar${collapsed ? ' collapsed' : ''}${mobileOpen ? ' open' : ''}`}
        id="sidebar"
        role="navigation"
        aria-label="Menu principal"
      >
        <div className="sidebar-logo">
          <div className="logo-icon" aria-hidden="true">
            hi
          </div>
          <div className="logo-text">
            <span className="logo-hi">hi</span> Conecta RH
            <small>Sistema Corporativo</small>
          </div>
          <button
            type="button"
            className="sidebar-collapse-btn"
            id="btn-collapse-sidebar"
            title={collapsed ? 'Expandir menu' : 'Colapsar menu'}
            aria-label="Colapsar menu lateral"
            onClick={toggleCollapse}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        <nav id="sidebar-nav" aria-label="Módulos do sistema">
          <div className="sidebar-search-wrapper">
            <input
              type="text"
              id="sidebar-search"
              className="sidebar-search"
              placeholder="Buscar módulo..."
              autoComplete="off"
              aria-label="Buscar módulo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="sidebar-search-icon" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
            </span>
          </div>

          {showPinned.length > 0 ? (
            <div className="sidebar-pinned">
              {showPinned.map((item) => (
                <NavItem key={`${item.group || ''}-${item.label}-${item.to || item.legacyHash || item.matrixKey}`} item={item} />
              ))}
            </div>
          ) : null}

          <div id="sidebar-sectors">
            {groups.map((g) => {
              const items = query ? g.items.filter((item) => matchesQuery(item, query)) : g.items
              if (items.length === 0) return null
              // Busca ativa abre os setores com resultado (como Sidebar.search).
              const open = query ? true : openSectors.includes(g.label)
              const hasActive = g.items.some(
                (item) => item.to && pathMatches(location.pathname, item.to, item.end),
              )
              return (
                <div
                  key={g.label}
                  className={`sidebar-sector${hasActive ? ' has-active' : ''}`}
                  data-sector={g.label}
                >
                  <button
                    type="button"
                    className="sidebar-sector-header"
                    aria-expanded={open}
                    title={g.label}
                    data-sigla={g.label.trim().charAt(0).toUpperCase()}
                    onClick={() => toggleSector(g.label)}
                  >
                    <span className="sector-label">{g.label}</span>
                    <span
                      className="sector-arrow"
                      aria-hidden="true"
                      style={{ transform: open ? 'rotate(45deg)' : 'rotate(-45deg)' }}
                    >
                      ›
                    </span>
                  </button>
                  <div className="sidebar-sector-items" style={{ display: open ? 'block' : 'none' }}>
                    {items.map((item) => (
                      <NavItem key={`${item.group || ''}-${item.label}-${item.to || item.legacyHash || item.matrixKey}`} item={item} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>

          {!query ? (
            <div className="sidebar-pinned sidebar-extra-links">
              <a
                className="sidebar-item"
                href={legacyAppUrl('#/')}
                title="Painel legado completo"
                data-sigla="L"
              >
                <span className="sidebar-item-label">Painel legado completo</span>
              </a>
              <NavLink
                className="sidebar-item"
                to="/carreiras"
                title="Carreiras públicas"
                data-sigla="C"
              >
                <span className="sidebar-item-label">Carreiras públicas</span>
              </NavLink>
            </div>
          ) : null}
        </nav>
      </aside>

      <div
        className="sidebar-overlay"
        id="sidebar-overlay"
        aria-hidden="true"
        onClick={() => setMobileOpen(false)}
      />

      <main className="main-content" id="mainContent">
        <header className="topbar">
          <button
            type="button"
            className="sidebar-toggle"
            aria-label="Alternar menu"
            onClick={toggleSidebar}
          >
            ☰
          </button>
          <div className="topbar-title" id="pageTitle">
            {crumb.setor ? (
              <div className="topbar-breadcrumb">
                <span className="breadcrumb-setor">{crumb.setor}</span>
                <span className="breadcrumb-sep">›</span>
                <span className="breadcrumb-page">{crumb.page}</span>
              </div>
            ) : (
              crumb.page
            )}
          </div>
          <div className="topbar-actions">
            <button
              type="button"
              className="btn-theme-toggle"
              id="btn-tema"
              title="Alternar tema"
              aria-label="Alternar tema claro/escuro"
              onClick={toggleTheme}
            />
            <div className="topbar-user-info" id="topbar-user" ref={userRef}>
              <div className="topbar-user-name" id="topbar-nome">
                {nome}
              </div>
              <button
                type="button"
                className="topbar-avatar"
                id="topbar-avatar"
                aria-haspopup="menu"
                aria-expanded={userMenu}
                aria-label="Menu do usuário"
                style={{ border: 0, padding: 0 }}
                onClick={() => setUserMenu((v) => !v)}
              >
                {iniciais}
              </button>
              {userMenu ? (
                <div className="user-dropdown" id="user-dropdown" role="menu">
                  <div className="user-dropdown-header" id="dropdown-header">
                    <strong>{nome}</strong>
                    <small>{user?.email || perfilLabel}</small>
                    {user?.email ? <small style={{ display: 'block' }}>{perfilLabel}</small> : null}
                  </div>
                  {canView('portal') ? (
                    <div
                      className="user-dropdown-item"
                      role="menuitem"
                      tabIndex={0}
                      onClick={() => navigate('/app/colaborador')}
                      onKeyDown={(e) => e.key === 'Enter' && navigate('/app/colaborador')}
                    >
                      🚀 Meu painel
                    </div>
                  ) : null}
                  <a
                    className="user-dropdown-item"
                    role="menuitem"
                    href={legacyAppUrl('#/')}
                    style={{ textDecoration: 'none' }}
                  >
                    📋 Painel legado
                  </a>
                  <div
                    className="user-dropdown-item"
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => void sair()}
                    onKeyDown={(e) => e.key === 'Enter' && void sair()}
                  >
                    🚪 Sair
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <div className="page-container" id="pageContainer" tabIndex={-1}>
          <Outlet />
        </div>
      </main>
    </div>
  )
}

function NavItem({ item }: { item: AppNavItem }) {
  const sigla = item.label.trim().charAt(0).toUpperCase()
  if (item.to) {
    return (
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) =>
          isActive ? 'sidebar-item sidebar-item--active' : 'sidebar-item'
        }
        title={item.label}
        data-sigla={sigla}
      >
        <span className="sidebar-item-label">{item.label}</span>
      </NavLink>
    )
  }
  const hash = item.legacyHash || `#/${item.matrixKey}`
  return (
    <a
      href={legacyAppUrl(hash)}
      className="sidebar-item"
      title={`${item.label} (legado)`}
      data-sigla={sigla}
    >
      <span className="sidebar-item-label">
        {item.label}
        <span className="sidebar-badge badge-soon">legado</span>
      </span>
    </a>
  )
}

/** "Início" e "Meu painel" ficam fixos no topo (como o "Meu painel" do Sidebar.js). */
function splitNav(items: AppNavItem[]) {
  const pinned: AppNavItem[] = []
  const order: string[] = []
  const map = new Map<string, AppNavItem[]>()
  for (const item of items) {
    if (item.matrixKey === 'dashboard' || item.matrixKey === 'portal') {
      pinned.push(item)
      continue
    }
    const g = item.group || 'Outros'
    if (!map.has(g)) {
      map.set(g, [])
      order.push(g)
    }
    map.get(g)!.push(item)
  }
  return { pinned, groups: order.map((label) => ({ label, items: map.get(label)! })) }
}

function matchesQuery(item: AppNavItem, query: string) {
  return !query || item.label.toLowerCase().includes(query)
}

function pathMatches(pathname: string, to: string, end?: boolean) {
  if (end) return pathname === to
  return pathname === to || pathname.startsWith(`${to}/`)
}

function crumbFromPath(pathname: string, nav: AppNavItem[]): { setor?: string; page: string } {
  const hit = nav.find((item) => item.to && pathMatches(pathname, item.to, item.end))
  if (hit) {
    const isPinned = hit.matrixKey === 'dashboard' || hit.matrixKey === 'portal'
    return { setor: isPinned ? undefined : hit.group, page: hit.label }
  }
  if (pathname.startsWith('/app/recrutamento/')) {
    return { setor: 'Recrutamento e Seleção', page: 'Vaga' }
  }
  if (pathname.startsWith('/app/rh/')) {
    return { setor: 'Cadastro', page: 'Colaborador' }
  }
  if (pathname.startsWith('/app/td/')) {
    return { setor: 'Treinamento e Desenvolvimento', page: 'T&D' }
  }
  if (pathname.startsWith('/app/sst/')) {
    return { setor: 'Saúde e Segurança', page: 'SST' }
  }
  return { page: 'hi Conecta RH' }
}

function initials(nome: string) {
  const parts = nome.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'HI'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}
